import React, { useState } from "react";
import {
  Image,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";

import EventImageModal from "../Images/EvenImageModal";

import TagItem from "@/Components/Cards/TagItem";
import { type EventImage, type Gallery } from "@/schemas/events/EventMedia";

type Props = {
  gallery: Gallery;
};

// TODO; this component needs to be clean up to be more general.

const spacing = 8;
const columns = 2;

function EventGallery({ gallery }: Props): React.JSX.Element {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [image, setImage] = useState<EventImage | null>(
    gallery.images?.[0] ?? null,
  );

  const { width } = useWindowDimensions();

  // Calculate container width based on platform
  const containerWidth = Platform.select({
    web: Math.min(width, 600), // Maximum width of 600px on web
    default: width,
  });

  // Calculate image width
  // Todo; fix the bug with two columns taking into account the border.
  const image_width =
    ((containerWidth - spacing * (columns + 1)) / columns) * 0.9;

  const filteredImages = selectedTags.length
    ? gallery.images.filter((image) =>
        selectedTags.some((tag) => image.tags.includes(tag)),
      )
    : gallery.images.slice(0, 4);

  const additionalImageCount =
    !selectedTags.length && gallery.images.length > 4
      ? gallery.images.length - 4
      : 0;

  const toggleTag = (tagId: string): void => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const onSelectImage = (image: EventImage): void => {
    setImage(image);
    setIsModalVisible(true);
  };

  return (
    <View style={styles.grid}>
      {filteredImages.map((image, index) => (
        <Pressable
          key={image.id}
          style={[
            styles.container,
            { width: image_width, height: image_width * 0.75 },
            index === 3 && additionalImageCount > 0 && styles.darkerBackground,
          ]}
          onPress={() => onSelectImage(image)}
        >
          <Image
            source={image.uri}
            style={styles.image}
            alt={image.alt}
            accessible={true}
            accessibilityLabel={image.alt}
            accessibilityRole="image"
          />
          {index === 3 && additionalImageCount > 0 && (
            <View style={styles.titleOverlay}>
              <Text style={styles.overlayText}>+{additionalImageCount}</Text>
            </View>
          )}
        </Pressable>
      ))}
      <View style={styles.tagsContainer}>
        {gallery.tags.map((tag) => (
          <TagItem
            key={tag.id}
            tag={tag}
            isSelected={selectedTags.includes(tag.id)}
            onPress={toggleTag}
          />
        ))}
      </View>
      {isModalVisible && image && (
        <EventImageModal
          image={image}
          onClose={() => setIsModalVisible(false)}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  container: {
    marginHorizontal: spacing / 2,
    marginBottom: spacing,
  },
  titleOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.25)",
    borderRadius: 8,
    padding: 8,
    justifyContent: "center", // Center vertically
    alignItems: "center", // Center horizontally
  },
  image: {
    width: "100%",
    height: "100%",
    borderRadius: 8,
  },
  tagsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: spacing,
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
  darkerBackground: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  overlayText: {
    color: "#FFFFFF",
    fontSize: 40,
    fontWeight: "bold",
    alignSelf: "center",
  },
});

export default EventGallery;
